import { RefObject, useEffect } from 'react';
import {
  SchemaFilterType,
  SelectItemProps,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
  useScroll,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { filtersSchema, schema } from '../variables/list';
import {
  useLazyGetAccountsPspPartnersQuery,
  useLazyGetExternalAccountsListQuery,
  useLazyGetAccountsCASPQuery,
} from '../../store/services';
import useGetAccountsAutocompleteList from '../../hooks/use-get-accounts';
import { PspPartner, ExternalAccount } from '../../types';
import {
  clearAccountsState,
  setTakeCount as setStoreTakeCount,
} from '../../store/slice';

const useListFunctions = ({
  scrollRef,
  pspPartners,
  externalAccounts,
  search,
  takeCount,
  setTakeCount,
}: {
  scrollRef: RefObject<HTMLDivElement | null>;
  pspPartners?: PspPartner[];
  externalAccounts?: ExternalAccount[];
  search?: string;
  takeCount: number;
  setTakeCount: (data: number) => void;
}) => {
  const dispatch = useAppDispatch();

  const { casp, openPayNotificationVisible } = useAppSelector(
    (state) => state.externalAccounts,
  );

  const prevOpenPayNotificationVisible = usePrevious(
    openPayNotificationVisible,
  );

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const [getExternalAccountsList, { isLoading, isFetching }] =
    useLazyGetExternalAccountsListQuery();
  const [getAccountsPspPartners] = useLazyGetAccountsPspPartnersQuery();
  const [getAccountsCASP] = useLazyGetAccountsCASPQuery();

  const prevFilters = usePrevious(filters);

  const { onFilter, setPage, errorList } = useGetList<ExternalAccount>({
    getList: getExternalAccountsList,
    onSortChange,
    onFilterChange,
    schema,
  });

  const { autocompleteList: accounts, onSearch: onSearchAccounts } =
    useGetAccountsAutocompleteList();

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'accountId') {
      onSearchAccounts(searchAutocomplete);
    }
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (!casp) {
      getAccountsCASP();
    }
  }, [casp]);

  useEffect(() => {
    setValues(
      'pspPartnerId',
      pspPartners?.map((item) => ({
        name: item.companyName,
        value: item.id,
      })) as SelectItemProps[],
    );

    setValues('accountId', accounts);
  }, [pspPartners, accounts]);

  useEffect(() => {
    getAccountsPspPartners();
  }, []);

  useEffect(() => {
    if (
      !externalAccounts &&
      !search &&
      !Object.keys(filters).length &&
      takeCount !== 0
    ) {
      onFilter({
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [filters, search, externalAccounts, takeCount]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        setTakeCount(Math.ceil(take + 3));

        dispatch(setStoreTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      takeCount !== 0 &&
      !externalAccounts &&
      search &&
      filters &&
      prevFilters &&
      Object.keys(prevFilters).length
    ) {
      const filtersValues: any = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      };

      onFilter(filtersValues);
    }
  }, [filters, search, externalAccounts, prevFilters, takeCount]);

  useEffect(() => {
    if (!openPayNotificationVisible && prevOpenPayNotificationVisible) {
      const filtersValues: any = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      };

      onFilter(filtersValues);
    }
  }, [openPayNotificationVisible, prevOpenPayNotificationVisible]);

  useEffect(
    () => () => {
      dispatch(clearAccountsState());
    },
    [],
  );

  return {
    handleSubmit,
    isLoading,
    setPage,
    errorList,
    filtersComponent,
    filters,
    onFilter,
    isFetching,
  };
};

export default useListFunctions;
